import React, { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import Plot from "react-plotly.js";
import * as Grid from "antd/lib/grid";
import LoadingState from "@/components/items-list/components/LoadingState";
import TimeAgo from "@/components/TimeAgo";
import Empty from "antd/lib/empty";
import Card from "antd/lib/card";
import { useTheme } from "@/components/ThemeProvider";

import "./AlertStats.less";

function AlertStats({ events }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAlerts: 0,
    uniqueUsers: 0,
    mostRecentTrigger: null,
    topDestinations: [],
    topQueries: [],
    mostTriggeredDay: "",
    mostFrequentHour: "",
  });
  const [chartData, setChartData] = useState(null);
  const { isDarkMode } = useTheme();
  const eventsRef = useRef(events);

  const calculateStats = useCallback((events) => {
    const totalAlerts = events.length;
    const uniqueUsers = new Set(events.map((event) => event.user?.name)).size;
    const mostRecentTrigger = events.length > 0 ? new Date(events[0].created_at).toLocaleString() : "N/A";

    const destinationCounts = events.reduce((acc, event) => {
      if (event.destination?.name) {
        acc[event.destination.name] = (acc[event.destination.name] || 0) + 1;
      }
      return acc;
    }, {});

    const topDestinations = Object.entries(destinationCounts)
      .sort(([, countA], [, countB]) => countB - countA)
      .slice(0, 5);

    const queryCounts = events.reduce((acc, event) => {
      if (event.query?.name) {
        acc[event.query.name] = (acc[event.query.name] || 0) + 1;
      }
      return acc;
    }, {});

    const topQueries = Object.entries(queryCounts)
      .sort(([, countA], [, countB]) => countB - countA)
      .slice(0, 5);

    const mostTriggeredDay = calculateMostTriggeredDay(events);
    const mostFrequentHour = calculateAlertFrequencyByHour(events);

    setStats({
      totalAlerts,
      uniqueUsers,
      mostRecentTrigger,
      topDestinations,
      topQueries,
      mostTriggeredDay,
      mostFrequentHour,
    });
  }, []);

  const generateChartData = useCallback((events) => {
    const dates = events.map((event) => new Date(event.created_at).toLocaleDateString());
    const dateCounts = dates.reduce((acc, date) => {
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const sortedDates = Object.keys(dateCounts).sort((a, b) => new Date(a) - new Date(b));
    const data = sortedDates.map((date) => dateCounts[date]);

    const errorDates = events
      .filter((event) => {
        const content = event.content;
        const additionalProperties =
          typeof event.additional_properties === "object"
            ? JSON.stringify(event.additional_properties)
            : event.additional_properties;

        return (
          (content && content.toLowerCase().includes("error")) ||
          (additionalProperties && additionalProperties.toLowerCase().includes("error"))
        );
      })
      .map((event) => new Date(event.created_at).toLocaleDateString());

    const errorData = sortedDates
      .map((date) => ({
        x: date,
        y: errorDates.includes(date) ? dateCounts[date] : null,
      }))
      .filter((d) => d.y !== null);

    const barTrace = {
      x: sortedDates,
      y: data,
      type: "bar",
      marker: {
        color: "rgba(155,141,204,1)",
        opacity: 0.6,
      },
      name: "Total Alerts",
    };

    const errorTrace = {
      x: errorData.map((d) => d.x),
      y: errorData.map((d) => d.y),
      mode: "markers",
      type: "scatter",
      marker: {
        color: "rgba(255, 120, 100, 1)",
        size: 12,
      },
      name: "Errors",
      showlegend: true,
    };

    setChartData([barTrace, errorTrace]);
  }, [isDarkMode]);

  useEffect(() => {
    if (events === null) {
      setLoading(true);
    } else if (events.length === 0) {
      setLoading(false);
    } else {
      eventsRef.current = orderByCreatedAt(events);
      calculateStats(eventsRef.current);
      generateChartData(eventsRef.current);
      setLoading(false);
    }
  }, [events, calculateStats, generateChartData]);

  const orderByCreatedAt = (events) => {
    return events.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const calculateMostTriggeredDay = (events) => {
    const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const dayCounts = events.reduce((acc, event) => {
      const day = new Date(event.created_at).getDay();
      acc[day] = (acc[day] || 0) + 1;
      return acc;
    }, {});

    const mostTriggeredDay = Object.keys(dayCounts).reduce((a, b) => (dayCounts[a] > dayCounts[b] ? a : b));
    return daysOfWeek[mostTriggeredDay];
  };

  const calculateAlertFrequencyByHour = (events) => {
    const hourCounts = events.reduce((acc, event) => {
      const hour = new Date(event.created_at).getHours();
      acc[hour] = (acc[hour] || 0) + 1;
      return acc;
    }, {});

    const mostFrequentHour = Object.keys(hourCounts).reduce((a, b) => (hourCounts[a] > hourCounts[b] ? a : b));
    return `${mostFrequentHour}:00 - ${parseInt(mostFrequentHour, 10) + 1}:00`;
  };

  const watermarkImage = {
    source: "/static/images/redash_icon_small.png",
    xref: "paper",
    yref: "paper",
    x: 0.5,
    y: 0.5,
    sizex: 0.4,
    sizey: 0.4,
    opacity: 0.4,
    xanchor: "center",
    yanchor: "middle",
    layer: "below"
  };

  return (
    <div className={`alert-statistics ${isDarkMode ? 'dark-mode' : ''}`}>
      <h3 className={`statistics-title ${isDarkMode ? 'dark-mode' : ''}`}>Alert Statistics</h3>
      {loading ? (
        <LoadingState />
      ) : events.length === 0 ? (
        <Empty description="No Data Available" />
      ) : (
        <div className={`bg-white tiled p-20 ${isDarkMode ? 'dark-mode' : ''}`}>
          <Grid.Row gutter={16} className={`tiled bg-white p-20 ${isDarkMode ? 'dark-mode' : ''}`}>
            <Grid.Col xs={24} md={12} className="mb-4 d-flex">
              <div className={`statistics-panel flex-fill ${isDarkMode ? 'dark-mode' : ''}`}>
                <Card title="Alert Summary" bordered={false} className={`mb-4 ${isDarkMode ? 'dark-mode' : ''}`}>
                  <p>Total Alerts: {stats.totalAlerts}</p>
                  <p>Unique Users: {stats.uniqueUsers}</p>
                  <p>
                    Most Recent Trigger: {stats.mostRecentTrigger} (
                    <TimeAgo date={stats.mostRecentTrigger} />)
                  </p>
                  <p>Most Triggered Day: {stats.mostTriggeredDay}</p>
                  <p>Most Frequent Hour: {stats.mostFrequentHour}</p>
                </Card>
                <Card title="Top Destinations" bordered={false} className={`mb-4 ${isDarkMode ? 'dark-mode' : ''}`}>
                  <ul className="list-unstyled">
                    {stats.topDestinations.map(([destination, count]) => (
                      <li key={destination}>
                        {destination}: {count}
                      </li>
                    ))}
                  </ul>
                </Card>
                <Card title="Top Queries" bordered={false} className={`${isDarkMode ? 'dark-mode' : ''}`}>
                  <ul className="list-unstyled">
                    {stats.topQueries.map(([query, count]) => (
                      <li key={query}>
                        {query}: {count}
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
            </Grid.Col>
            <Grid.Col xs={24} md={12} className="mb-4 d-flex align-items-center justify-content-center">
              <div className={`chart-panel w-100 ${isDarkMode ? 'dark-mode' : ''}`}>
                <div className={`chart-container ${isDarkMode ? 'dark-mode' : ''}`}>
                  {chartData && (
                    <Plot
                      data={chartData}
                      layout={{
                        xaxis: {
                          title: "Date",
                          tickformat: "%d-%m-%Y",
                          tickangle: -45,
                          color: isDarkMode ? '#ffffff' : '#000000',
                        },
                        yaxis: { 
                          title: "Number of Alerts",
                          color: isDarkMode ? '#ffffff' : '#000000',
                        },
                        title: {
                          text: "Alerts Over Time",
                          font: {
                            color: isDarkMode ? '#ffffff' : '#000000',
                          },
                        },
                        barmode: "overlay",
                        legend: {
                          orientation: "h",
                          y: 1.1,
                          font: {
                            color: isDarkMode ? '#ffffff' : '#000000',
                          },
                        },
                        images: [watermarkImage],
                        paper_bgcolor: isDarkMode ? 'rgba(0,0,0,0)' : '#ffffff',
                        plot_bgcolor: isDarkMode ? 'rgba(0,0,0,0)' : '#ffffff',
                      }}
                      config={{
                        displaylogo: false,
                        showLink: false,
                      }}
                      style={{ width: "100%", height: "100%" }}
                    />
                  )}
                </div>
              </div>
            </Grid.Col>
          </Grid.Row>
        </div>
      )}
    </div>
  );
}

AlertStats.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      created_at: PropTypes.string.isRequired,
      user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
      alert_type: PropTypes.string.isRequired,
      destination: PropTypes.shape({
        name: PropTypes.string,
      }),
      query: PropTypes.shape({
        name: PropTypes.string,
      }),
    })
  ).isRequired,
};

export default AlertStats;
